/*
 * LINFINITY ENGINEERING INVOICE TEMPLATE
 * =====================================
 * 
 * This React PDF template generates A4 invoices matching the exact HTML sample layout.
 * Based on Invoice 25070-1 with 7-column table structure and specific color scheme.
 * 
 * LAYOUT STRUCTURE:
 * 1. Header Section (Logo + Invoice Info Table)
 * 2. Total Due + Client Info Boxes (Side by side)
 * 3. 7-Column Project Table with alternating row colors
 * 4. Payment Methods + Totals Section
 * 5. Legal Footer with company details
 * 
 * COLOR SCHEME (Matches HTML sample exactly):
 * - #808080: Main gray (client box background, dark table columns)
 * - #C7C7C7: Light gray header (Project, Address, Item columns)
 * - #E0E0E0: Lightest alternating rows (columns 1-3)
 * - #D6D6D6: Medium alternating rows (columns 4-7)
 * - #CDCDCD: Second alternating pattern
 * - #333: Dark gray (Total Due box)
 * - #d32f2f: Red (payment reference text)
 * 
 * TABLE STRUCTURE (7 columns, widths in points):
 * Column 1: Job Number (56pt) - Project or inspection job number
 * Column 2: Address (132pt) - Project location
 * Column 3: Item (118pt) - Service description
 * Column 4: RATE (96pt) - Hourly/fixed rate
 * Column 5: Qty (93pt) - Quantity/hours
 * Column 6: TOTAL (88pt) - Line total amount
 * Column 7: Excl. GST (56pt) - Tax indicator
 * 
 * CUSTOMIZATION GUIDE:
 * - Adjust COLORS object for different color schemes
 * - Modify column widths in th/td styles (must total ~722pt)
 * - Change fonts in page.fontFamily or individual fontSize
 * - Update company details in footer section
 * - Modify P (padding) constant for overall page margins
 * 
 * TESTING: http://localhost:3007/pdf-test
 */

import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { buildInvoicePaymentReference } from '@/utils/invoiceReference';
import { formatStripePaymentLabelForPdf, getVisibleStripePaymentUrl } from '@/utils/stripePaymentLabel';
import LogoPNG from '../../assets/logos/LE Logo - small - resized 2.png';
import EquilibriumLogoPNG from '../../assets/logos/ee-logo-150-pixels.png';

interface InvoiceItem {
  categoryName?: string;
  description: string;
  rate?: number;
  quantity?: number;
  total: number;
  exclGst?: boolean;
}
interface InvoiceData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  rfiTicketNumber?: string;
  stripePaymentUrl?: string;
  stripeSurchargeEnabled?: boolean;
  entity?: string;
  senderDisplayName?: string;
  senderAddress?: string;
  senderMobile?: string;
  senderEmail?: string;
  senderPayId?: string;
  senderBsb?: string;
  senderAccountNumber?: string;
  senderAccountName?: string;
  clientName: string;
  companyName?: string;
  clientAddress: string;
  clientMobile?: string;
  clientEmail?: string;
  invoiceDetails?: string;
  projectNumber: string;
  projectLocation: string;
  totalDue: number;
  subtotal: number;
  gstAmount: number;
  paidAmount: number;
  items?: InvoiceItem[];
}
interface InvoiceTemplateProps {
  data: InvoiceData;
  showPaymentClaim?: boolean;
}

// COLOR DEFINITIONS (Based on HTML sample analysis)
const COLORS = {
  black: '#000',           // Text color for most content
  dark: '#333',            // Total Due box background (dark gray)
  gray: '#808080',         // Main gray: Client info box, table columns 4-7 header
  grayHead: '#C7C7C7',     // Light gray: Table header for columns 1-3 (Project, Address, Item)
  grayL1: '#E0E0E0',       // Lightest: Alternating rows columns 1-3 (odd rows)
  grayL2: '#D6D6D6',       // Medium: Alternating rows columns 4-7 (odd rows)
  grayL3: '#CDCDCD',       // Second pattern: Alternating rows (even rows)
  white: '#fff',           // Background and border colors
  red: '#d32f2f'           // Payment reference text highlight
};

type InvoiceTheme = {
  accentDark: string;
  accentMid: string;
  accentLight: string;
  rowLeftOdd: string;
  rowRightOdd: string;
  rowEven: string;
  border: string;
  reference: string;
  claim: string;
};

function resolveInvoiceTheme(entity?: string): InvoiceTheme {
  const normalized = String(entity || '').trim().toLowerCase();

  if (normalized === 'equilibrium engineering pty ltd') {
    return {
      accentDark: COLORS.dark,
      accentMid: COLORS.gray,
      accentLight: COLORS.grayHead,
      rowLeftOdd: COLORS.grayL1,
      rowRightOdd: COLORS.grayL2,
      rowEven: COLORS.grayL3,
      border: COLORS.gray,
      reference: COLORS.red,
      claim: '#666',
    };
  }

  return {
    accentDark: '#2E5F93',
    accentMid: '#5D87B5',
    accentLight: '#D7E5F3',
    rowLeftOdd: '#EDF4FA',
    rowRightOdd: '#E1EDF8',
    rowEven: '#D4E3F0',
    border: '#5D87B5',
    reference: '#2E5F93',
    claim: '#5D87B5',
  };
}

// A4 PAGE DIMENSIONS: 595 x 842 points
const A4_PAGE_WIDTH = 595.28;
const P = 20;
const LOGO_AREA_WIDTH = 168;
const TOTAL_DUE_BOX_WIDTH = 126;
const TOTALS_SECTION_WIDTH = 150;
const TABLE_PROJECT_WIDTH = 56;
const TABLE_ADDRESS_WIDTH = 132;
const TABLE_ITEM_WIDTH = 118;
const TABLE_RATE_WIDTH = 68;
const TABLE_QTY_WIDTH = 36;
const TABLE_TOTAL_WIDTH = 76;
const TABLE_EXCL_WIDTH = 54;
const TABLE_WIDTH =
  TABLE_PROJECT_WIDTH +
  TABLE_ADDRESS_WIDTH +
  TABLE_ITEM_WIDTH +
  TABLE_RATE_WIDTH +
  TABLE_QTY_WIDTH +
  TABLE_TOTAL_WIDTH +
  TABLE_EXCL_WIDTH;
const TABLE_LEFT_SECTION_WIDTH = TABLE_PROJECT_WIDTH + TABLE_ADDRESS_WIDTH + TABLE_ITEM_WIDTH;
const TABLE_RIGHT_SECTION_WIDTH = TABLE_RATE_WIDTH + TABLE_QTY_WIDTH + TABLE_TOTAL_WIDTH + TABLE_EXCL_WIDTH;
const HEADER_COLUMN_WIDTH = TABLE_RIGHT_SECTION_WIDTH / 3;
const CONTENT_WIDTH = A4_PAGE_WIDTH - P * 2;
const CONTENT_GUTTER = Math.max(0, (CONTENT_WIDTH - TABLE_WIDTH) / 2);

const styles = StyleSheet.create({
  // ===== PAGE SETUP =====
  page: {
    backgroundColor: COLORS.white,
    padding: P,                    // Overall page margins
    fontFamily: 'Helvetica'        // Change to 'Times-Roman' or 'Courier' for different fonts
  },

  // ===== HEADER SECTION (Logo + Invoice Table) =====
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    paddingLeft: CONTENT_GUTTER,
    paddingRight: CONTENT_GUTTER,
  },
  headerLeft: {
    width: TABLE_LEFT_SECTION_WIDTH,
    justifyContent: 'flex-start',
  },
  headerRight: {
    width: TABLE_RIGHT_SECTION_WIDTH,
    justifyContent: 'flex-start',
  },
  logoArea: { width: LOGO_AREA_WIDTH, height: 50, justifyContent: 'center' },

  invoiceHeader: { width: TABLE_RIGHT_SECTION_WIDTH },
  invoiceHeaderTable: { marginBottom: 8 },
  invoiceTitle: {
    fontSize: 16,
    color: COLORS.black,
    paddingTop: 2, paddingRight: 3, paddingBottom: 2, paddingLeft: 3,
    borderBottomWidth: 2, borderBottomColor: COLORS.gray
  },
  invoiceHeaderRow: {
    flexDirection: 'row',
    borderTopWidth: 2, borderTopColor: COLORS.gray,
    paddingTop: 6, paddingBottom: 6
  },
  invoiceDataRow: { flexDirection: 'row', paddingTop: 3, paddingBottom: 3 },
  invoiceHeaderCell: { width: HEADER_COLUMN_WIDTH, fontSize: 10, color: COLORS.black, paddingLeft: 2 },
  invoiceDataCell:  { width: HEADER_COLUMN_WIDTH, fontSize: 10, color: COLORS.black, paddingLeft: 2, fontWeight: 'bold' },

  // ===== Totals + Client box row =====
  totalDueSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    marginLeft: CONTENT_GUTTER,
    marginRight: CONTENT_GUTTER,
  },
  totalDueLeft: {
    width: TABLE_LEFT_SECTION_WIDTH,
    justifyContent: 'flex-start',
  },
  totalDueRight: {
    width: TABLE_RIGHT_SECTION_WIDTH,
    justifyContent: 'flex-start',
  },
  totalDueBox: {
    width: TOTAL_DUE_BOX_WIDTH,
    backgroundColor: COLORS.dark,
    paddingTop: 8, paddingRight: 10, paddingBottom: 8, paddingLeft: 10,
  },
  totalDueLabel: { fontSize: 12, color: COLORS.white, marginBottom: 1 },
  totalDueAmount:{ fontSize: 18, color: COLORS.white, fontWeight: 'bold' },

  clientInfoBox: {
    width: TABLE_RIGHT_SECTION_WIDTH,
    backgroundColor: COLORS.gray,
    paddingTop: 5, paddingRight: 6, paddingBottom: 5, paddingLeft: 6,
    color: COLORS.white,
  },
  clientLabel:   { fontSize: 10, marginBottom: 2 },
  clientName:    { fontSize: 10, fontWeight: 'bold', marginBottom: 2 },
  clientCompany: { fontSize: 9, marginBottom: 3 },
  clientAddress: { fontSize: 10, lineHeight: 1.2 },
  clientContactBlock: { marginTop: 3 },
  clientContactLine: { fontSize: 10, lineHeight: 1.2 },

  // ===== 7-COLUMN PROJECT TABLE =====
  // Use one shared gutter so the table, header, payment block, and footer align to the same frame.
  projectTable: { 
    marginTop: 10, marginBottom: 10, 
    marginLeft: CONTENT_GUTTER,
    marginRight: CONTENT_GUTTER,
  },
  tableHeader: { 
    flexDirection: 'row', 
    minHeight: 34
  },

  // COLUMN 1: Project Number (121pt wide)
  thProject: {
    width: TABLE_PROJECT_WIDTH, backgroundColor: COLORS.grayHead,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 2: Address (152pt wide)
  thEmpty: {
    width: TABLE_ADDRESS_WIDTH, backgroundColor: COLORS.grayHead,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 3: Item Description (116pt wide) 
  thItem: {
    width: TABLE_ITEM_WIDTH, backgroundColor: COLORS.grayHead,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
    borderRightWidth: 1, borderRightColor: COLORS.white,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 4: Rate (96pt wide)
  thRate: {
    width: TABLE_RATE_WIDTH, backgroundColor: COLORS.gray,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 6, paddingRight: 6,
    borderLeftWidth: 1, borderLeftColor: COLORS.white,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 5: Quantity (93pt wide)
  thQty: {
    width: TABLE_QTY_WIDTH, backgroundColor: COLORS.gray,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 4, paddingRight: 4,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 6: Total Amount (88pt wide)
  thTotal: {
    width: TABLE_TOTAL_WIDTH, backgroundColor: COLORS.gray,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 6, paddingRight: 6,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // COLUMN 7: GST Indicator (56pt wide)
  thExcl: {
    width: TABLE_EXCL_WIDTH, backgroundColor: COLORS.gray,
    justifyContent: 'center', alignItems: 'center',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 4, paddingRight: 4,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  // HEADER TEXT STYLES
  thText: { fontSize: 9, fontWeight: 'bold', color: COLORS.black, textAlign: 'center' },
  thTextLight: { fontSize: 8.5, fontWeight: 'bold', color: COLORS.white, textAlign: 'center' },

  // ===== TABLE DATA ROWS =====
  tableRow: {
    flexDirection: 'row', 
    minHeight: 28,
    borderTopWidth: 1, borderTopColor: COLORS.white,
    borderBottomWidth: 1, borderBottomColor: COLORS.white
  },
  tdProject: {
    width: TABLE_PROJECT_WIDTH, justifyContent: 'center',
    paddingLeft: 8, paddingRight: 8,
    fontSize: 8.5, fontWeight: 'bold', color: COLORS.black,
    textAlign: 'center',
  },
  tdLocation: {
    width: TABLE_ADDRESS_WIDTH, justifyContent: 'center',
    paddingLeft: 8, paddingRight: 8,
    fontSize: 8.5, color: COLORS.black
  },
  tdItem: {
    width: TABLE_ITEM_WIDTH, justifyContent: 'center',
    textAlign: 'center',
    paddingTop: 6, paddingBottom: 6,
    fontSize: 8.5, color: COLORS.black,
    borderRightWidth: 1, borderRightColor: COLORS.white
  },
  tdRate:  { width: TABLE_RATE_WIDTH },
  tdQty:   { width: TABLE_QTY_WIDTH },
  tdTotal: {
    width: TABLE_TOTAL_WIDTH, justifyContent: 'center',
    textAlign: 'right', paddingRight: 8,
    fontSize: 9, fontWeight: 'bold', color: COLORS.black
  },
  tdExcl: {
    width: TABLE_EXCL_WIDTH, justifyContent: 'center', alignItems: 'center',
    textAlign: 'center',
    fontSize: 7.5, color: COLORS.black
  },

  // second line style (span)
  spanRowLeft: {
    width: 389, justifyContent: 'center',
    paddingLeft: 19, paddingRight: 19,
    borderRightWidth: 1, borderRightColor: COLORS.white
  },

  // ===== Payment + Totals =====
  paymentSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    marginLeft: CONTENT_GUTTER,
    marginRight: CONTENT_GUTTER,
  },
  paymentMethods: { flex: 1, paddingRight: 16 },
  paymentTitle: { fontSize: 10, fontWeight: 'bold', marginBottom: 6 },
  paymentText: { fontSize: 9, lineHeight: 1.3, marginBottom: 3 },
  paymentLinkText: {
    fontSize: 9,
    lineHeight: 1.3,
    marginBottom: 3,
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  referenceText: { color: COLORS.red, fontWeight: 'bold' },

  totalsSection: { width: TOTALS_SECTION_WIDTH },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', fontSize: 9, paddingTop: 3, paddingBottom: 3 },
  grandTotalRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    backgroundColor: COLORS.gray, color: COLORS.white,
    paddingTop: 3, paddingRight: 8, paddingBottom: 3, paddingLeft: 12,
    fontSize: 10, fontWeight: 'bold'
  },

  // ===== Legal/footers =====
  footerSpacer: { flexGrow: 1, minHeight: 16 },
  paymentClaim: {
    fontSize: 8,
    fontStyle: 'italic',
    color: '#666',
    marginTop: 5,
    marginBottom: 5,
    marginLeft: CONTENT_GUTTER,
    marginRight: CONTENT_GUTTER,
    textAlign: 'center'
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    paddingTop: 5,
    marginTop: 5,
    marginLeft: CONTENT_GUTTER,
    marginRight: CONTENT_GUTTER,
    fontSize: 8,
    color: '#666',
    textAlign: 'center'
  },
  footerLine: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
    marginBottom: 1,
  },
});

const InvoiceLogoPDF: React.FC<{ entity?: string }> = ({ entity }) => {
  const isEquilibrium = String(entity || '').trim().toLowerCase() === 'equilibrium engineering pty ltd';

  return (
    <View style={[styles.logoArea, isEquilibrium ? { width: LOGO_AREA_WIDTH + 40, height: 72 } : null]}>
      <Image
        src={isEquilibrium ? EquilibriumLogoPNG : LogoPNG}
        style={isEquilibrium ? { width: 208, height: 68 } : { width: 140, height: 42 }}
      />
    </View>
  );
};

const MIN_VISIBLE_ITEM_ROWS = 7;

function formatCurrencyValue(amount?: number) {
  const value = Number.isFinite(amount) ? Number(amount) : 0;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
}

function formatQuantityValue(quantity?: number) {
  if (!Number.isFinite(quantity)) return '';
  return Number.isInteger(quantity as number)
    ? String(quantity)
    : Number(quantity).toFixed(2);
}

function normalizeItemText(value?: string | null) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

type FormatInvoiceItemTextOptions = {
  includeInvoiceNotes?: boolean;
  invoiceNotes?: string | null;
};

export function formatInvoiceItemTextForPdf(
  item: Pick<InvoiceItem, 'categoryName' | 'description'>,
  options: FormatInvoiceItemTextOptions = {},
): string {
  const categoryName = normalizeItemText(item.categoryName);
  const description = normalizeItemText(item.description);
  const note = options.includeInvoiceNotes ? normalizeItemText(options.invoiceNotes) : '';

  const withNote = (itemText: string) => {
    if (!note) return itemText;
    if (!itemText) return note;
    if (normalizeItemText(itemText).toLowerCase().includes(note.toLowerCase())) return itemText;
    return `${itemText}\n${note}`;
  };

  if (!categoryName) return withNote(description);
  if (!description) return withNote(categoryName);
  if (description.localeCompare(categoryName, undefined, { sensitivity: 'accent' }) === 0) {
    return withNote(categoryName);
  }

  if (categoryName.toLowerCase() === 'structural variation') {
    return withNote(`${categoryName}:\n${description}`);
  }

  return withNote(`${categoryName}: ${description}`);
}

function parseLegacyInvoiceItems(invoiceDetails?: string): InvoiceItem[] {
  const raw = String(invoiceDetails || '').trim();
  if (!raw) return [];

  return raw
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(.*?)\s*\[\$(.+?)\]$/);
      const description = (match?.[1] || part).trim();
      const amount = Number(String(match?.[2] || '0').replace(/,/g, '').trim());
      const total = Number.isFinite(amount) ? amount : 0;
      return {
        description,
        rate: total,
        quantity: 1,
        total,
        exclGst: true,
      };
    });
}

export function formatProjectLocationForPdf(projectLocation?: string | null): string {
  const normalized = String(projectLocation || '').trim();
  if (!normalized) return '';

  const segments = normalized
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length >= 3) {
    const suburb = segments[segments.length - 2];
    const addressLine = stripDuplicateTrailingSuburb(segments.slice(0, -2).join(', '), suburb);
    const suburbLine = [suburb, segments[segments.length - 1]].filter(Boolean).join(', ');
    return `${addressLine},\n${suburbLine}`;
  }

  if (segments.length === 2) {
    return `${segments[0]},\n${segments[1]}`;
  }

  return normalized;
}

function normalizeAddressToken(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripDuplicateTrailingSuburb(addressLine: string, suburb: string): string {
  const normalizedAddress = String(addressLine || '').trim();
  const normalizedSuburb = String(suburb || '').trim();
  if (!normalizedAddress || !normalizedSuburb) return normalizedAddress;

  const addressTokens = normalizeAddressToken(normalizedAddress).split(' ').filter(Boolean);
  const suburbTokens = normalizeAddressToken(normalizedSuburb).split(' ').filter(Boolean);
  if (!addressTokens.length || !suburbTokens.length || addressTokens.length <= suburbTokens.length) {
    return normalizedAddress;
  }

  const addressTail = addressTokens.slice(-suburbTokens.length).join(' ');
  if (addressTail !== suburbTokens.join(' ')) return normalizedAddress;

  const suburbPattern = suburbTokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
  const duplicateTail = new RegExp(`(?:\\s|,)+${suburbPattern}\\s*$`, 'i');
  return normalizedAddress.replace(duplicateTail, '').replace(/[,\s]+$/, '').trim();
}

export function getInvoiceRowBackgrounds(
  rowIndex: number,
  theme: Pick<InvoiceTheme, 'rowLeftOdd' | 'rowRightOdd' | 'rowEven'>
): { left: string; right: string } {
  const isOddVisualRow = rowIndex % 2 === 0;
  if (isOddVisualRow) {
    return {
      left: theme.rowLeftOdd,
      right: theme.rowRightOdd,
    };
  }

  return {
    left: theme.rowEven,
    right: theme.rowEven,
  };
}

export function formatInvoiceFooterReference(invoiceNumber?: string | null): string {
  const normalized = String(invoiceNumber || '').trim();
  return `Invoice Reference: ${normalized || 'DRAFT'}`;
}

export function formatInvoiceClientMobileForPdf(clientMobile?: string | null): string {
  const normalized = String(clientMobile || '').trim();
  if (!normalized) return '';

  const digits = normalized.replace(/\D/g, '');
  if (digits && /^0+$/.test(digits) && /^[\d\s()+.-]+$/.test(normalized)) {
    return '';
  }

  return normalized;
}

export function resolveInvoiceTableJobNumber(data: Pick<InvoiceData, 'invoiceNumber' | 'projectNumber'>): string {
  const projectNumber = String(data.projectNumber || '').trim();
  if (projectNumber) return projectNumber;
  return isInspectionInvoiceNumber(data.invoiceNumber) ? String(data.invoiceNumber || '').trim() : '';
}

export function isInspectionInvoiceNumber(invoiceNumber?: string | null): boolean {
  return /^INX\b/i.test(String(invoiceNumber || '').trim());
}

export function resolveInvoiceTableProjectHeader(data: Pick<InvoiceData, 'invoiceNumber'>): string {
  return isInspectionInvoiceNumber(data.invoiceNumber) ? 'Job Number' : 'Project';
}

type SenderProfile = {
  displayName: string;
  address: string;
  mobile: string;
  email: string;
  payId?: string;
  bsb: string;
  accountNumber: string;
  accountName: string;
};

const LINFINITY_SENDER_DISPLAY_NAME = 'Linfinity Engineering Pty Ltd (ABN 64 427 178 431)';
const EQUILIBRIUM_SENDER_DISPLAY_NAME = 'Equilibrium Engineering Pty Ltd (ABN 24 624 618 435)';

function normalizeCompanyKey(value?: string | null): string {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('equilibrium engineering')) return 'equilibrium';
  if (normalized.includes('linfinity engineering')) return 'linfinity';
  return '';
}

export function formatInvoiceSenderDisplayNameForFooter(
  displayName?: string | null,
  entity?: string | null,
): string {
  const normalizedDisplayName = String(displayName || '').trim();
  const companyKey = normalizeCompanyKey(entity) || normalizeCompanyKey(normalizedDisplayName);

  if (companyKey === 'equilibrium') {
    return normalizedDisplayName && /\bABN\b/i.test(normalizedDisplayName)
      ? normalizedDisplayName
      : EQUILIBRIUM_SENDER_DISPLAY_NAME;
  }

  if (companyKey === 'linfinity') {
    return normalizedDisplayName && /\bABN\b/i.test(normalizedDisplayName)
      ? normalizedDisplayName
      : LINFINITY_SENDER_DISPLAY_NAME;
  }

  return normalizedDisplayName || LINFINITY_SENDER_DISPLAY_NAME;
}

function getSenderProfile(entity?: string): SenderProfile {
  const normalized = String(entity || '').trim().toLowerCase();
  if (normalized === 'equilibrium engineering pty ltd') {
    return {
      displayName: EQUILIBRIUM_SENDER_DISPLAY_NAME,
      address: '1/21 Barry Street, Bayswater VIC 3153',
      mobile: '0421 900 119',
      email: 'wdu@eqeng.com.au',
      bsb: '063-109',
      accountNumber: '1279-4847',
      accountName: 'Equilibrium Engineering Pty Ltd (ACN: 624 618 435)',
    };
  }

  return {
    displayName: LINFINITY_SENDER_DISPLAY_NAME,
    address: 'Suite 261, Tenancy 111, Camberwell Place, 793 Burke Road, Camberwell, VIC 3124',
    mobile: '0468 886 926',
    email: 'yl@linfinityeng.com',
    payId: 'YL@linfinityeng.com',
    bsb: '013-479',
    accountNumber: '4177-69018',
    accountName: 'Linfinity Engineering Pty Ltd',
  };
}

function resolveSenderProfile(data: InvoiceData): SenderProfile {
  const fallback = getSenderProfile(data.entity);
  const hasStoredSenderFields = Boolean(
    data.senderDisplayName ||
    data.senderAddress ||
    data.senderMobile ||
    data.senderEmail ||
    data.senderPayId ||
    data.senderBsb ||
    data.senderAccountNumber ||
    data.senderAccountName
  );

  if (hasStoredSenderFields) {
    return {
      displayName: formatInvoiceSenderDisplayNameForFooter(data.senderDisplayName || fallback.displayName, data.entity),
      address: data.senderAddress || fallback.address,
      mobile: data.senderMobile || fallback.mobile,
      email: data.senderEmail || fallback.email,
      payId: data.senderPayId || fallback.payId,
      bsb: data.senderBsb || fallback.bsb,
      accountNumber: data.senderAccountNumber || fallback.accountNumber,
      accountName: data.senderAccountName || fallback.accountName,
    };
  }

  return {
    ...fallback,
    displayName: formatInvoiceSenderDisplayNameForFooter(fallback.displayName, data.entity),
  };
}

/*
 * MANUAL CUSTOMIZATION GUIDE:
 * ===========================
 * 
 * 1. CHANGE COLORS:
 *    - Edit the COLORS object above (lines 77-87)
 *    - Use hex codes: #FF0000 (red), #00FF00 (green), etc.
 *
 * 2. ADJUST TABLE LAYOUT:
 *    - Column widths: Modify thProject, thEmpty, thItem, etc. (lines 166-215)
 *    - CRITICAL: Keep total width = 722pt or adjust marginLeft accordingly
 *    - Row heights: Change tableRow.height (line 223) and header.height (line 162)
 *
 * 3. FONTS & SIZES:
 *    - Main font: Change page.fontFamily (line 97)
 *    - Individual sizes: Modify fontSize in specific styles (e.g., line 217-218)
 *    - Available fonts: 'Helvetica', 'Times-Roman', 'Courier'
 *
 * 4. SPACING & MARGINS:
 *    - Page margins: Adjust P constant (line 90) 
 *    - Section gaps: Modify marginTop/marginBottom in various styles
 *    - Table position: Adjust projectTable.marginLeft (line 158)
 *
 * 5. COMPANY DETAILS:
 *    - Bank info: Lines 408-419 (Payment Methods section)
 *    - Footer: Lines 449-457 (company name, address, contact)
 *    - Logo: Change LogoPNG import path (line 45)
 *
 * 6. ALTERNATING ROWS:
 *    - Pattern controlled in lines 393-404 (map function)
 *    - Colors: grayL1, grayL2, grayL3 for different alternating patterns
 *
 * TEST CHANGES: Save file → Refresh http://localhost:3007/pdf-test → Click Preview PDF
 */

const InvoiceTemplate: React.FC<InvoiceTemplateProps> = ({ data, showPaymentClaim = true }) => {
  const invoiceItems = data.items && data.items.length > 0
    ? data.items
    : parseLegacyInvoiceItems(data.invoiceDetails);
  const fillerRowCount = Math.max(0, MIN_VISIBLE_ITEM_ROWS - invoiceItems.length);
  const senderProfile = resolveSenderProfile(data);
  const theme = resolveInvoiceTheme(data.entity);
  const visibleStripePaymentUrl = getVisibleStripePaymentUrl(data.entity, data.stripePaymentUrl);
  const projectLocationText = formatProjectLocationForPdf(data.projectLocation);
  const tableProjectHeader = resolveInvoiceTableProjectHeader(data);
  const tableJobNumber = resolveInvoiceTableJobNumber(data);
  const clientMobile = formatInvoiceClientMobileForPdf(data.clientMobile);
  const paymentReference = String(data.invoiceNumber || '').trim()
    || buildInvoicePaymentReference('', data.rfiTicketNumber)
    || data.invoiceNumber;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <InvoiceLogoPDF entity={data.entity} />
          </View>
          <View style={styles.headerRight}>
            <View style={styles.invoiceHeader}>
              <View style={styles.invoiceHeaderTable}>
                <Text style={[styles.invoiceTitle, { borderBottomColor: theme.border }]}>INVOICE</Text>
                <View style={[styles.invoiceHeaderRow, { borderTopColor: theme.border }]}>
                  <Text style={styles.invoiceHeaderCell}>Invoice no.</Text>
                  <Text style={styles.invoiceHeaderCell}>Date</Text>
                  <Text style={styles.invoiceHeaderCell}>Due Date</Text>
                </View>
                <View style={styles.invoiceDataRow}>
                  <Text style={styles.invoiceDataCell}>{data.invoiceNumber}</Text>
                  <Text style={styles.invoiceDataCell}>{formatDate(data.issueDate)}</Text>
                  <Text style={styles.invoiceDataCell}>{formatDate(data.dueDate)}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Total due + client box */}
        <View style={styles.totalDueSection}>
          <View style={styles.totalDueLeft}>
            <View style={[styles.totalDueBox, { backgroundColor: theme.accentDark }]}>
              <Text style={styles.totalDueLabel}>TOTAL DUE</Text>
              <Text style={styles.totalDueAmount}>{formatCurrencyValue(data.totalDue)}</Text>
            </View>
          </View>
          <View style={styles.totalDueRight}>
            <View style={[styles.clientInfoBox, { backgroundColor: theme.accentMid }]}>
              <Text style={styles.clientLabel}>Invoice to:</Text>
              <Text style={styles.clientName}>{data.clientName}</Text>
              {data.companyName ? <Text style={styles.clientCompany}>{data.companyName}</Text> : null}
              <Text style={styles.clientAddress}>{data.clientAddress}</Text>
              {(clientMobile || data.clientEmail) ? (
                <View style={styles.clientContactBlock}>
                  {clientMobile ? <Text style={styles.clientContactLine}>{`M: ${clientMobile}`}</Text> : null}
                  {data.clientEmail ? <Text style={styles.clientContactLine}>{`E: ${data.clientEmail}`}</Text> : null}
                </View>
              ) : null}
            </View>
          </View>
        </View>

        {/* Table */}
        <View style={styles.projectTable}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.thProject, { backgroundColor: theme.accentLight }]}><Text style={styles.thText}>{tableProjectHeader}</Text></View>
            <View style={[styles.thEmpty, { backgroundColor: theme.accentLight }]}><Text style={styles.thText}>Address</Text></View>
            <View style={[styles.thItem, { backgroundColor: theme.accentLight }]}><Text style={styles.thText}>Item</Text></View>
            <View style={[styles.thRate, { backgroundColor: theme.accentMid }]}><Text style={styles.thTextLight}>RATE</Text></View>
            <View style={[styles.thQty, { backgroundColor: theme.accentMid }]}><Text style={styles.thTextLight}>Qty</Text></View>
            <View style={[styles.thTotal, { backgroundColor: theme.accentMid }]}><Text style={styles.thTextLight}>TOTAL</Text></View>
            <View style={[styles.thExcl, { backgroundColor: theme.accentMid }]}>
              <Text style={styles.thTextLight}>Excl. GST</Text>
            </View>
          </View>

          {/* Item rows */}
          {invoiceItems.map((item, index) => {
            const rowBackgrounds = getInvoiceRowBackgrounds(index, theme);
            const isFirstRow = index === 0;
            const itemText = formatInvoiceItemTextForPdf(item, {
              includeInvoiceNotes: isFirstRow && isInspectionInvoiceNumber(data.invoiceNumber),
              invoiceNotes: data.notes,
            });

            return (
              <View key={`${item.description}-${index}`} style={styles.tableRow}>
                <View style={[styles.tdProject, { backgroundColor: rowBackgrounds.left }]}>
                  <Text>{isFirstRow ? tableJobNumber : ''}</Text>
                </View>
                <View style={[styles.tdLocation, { backgroundColor: rowBackgrounds.left }]}>
                  <Text>{isFirstRow ? projectLocationText : ''}</Text>
                </View>
                <View style={[styles.tdItem, { backgroundColor: rowBackgrounds.left }]}>
                  <Text>{itemText}</Text>
                </View>
                <View style={[styles.tdRate, { backgroundColor: rowBackgrounds.right, justifyContent: 'center', textAlign: 'center' }]}>
                  <Text style={{ fontSize: 10, color: COLORS.black, textAlign: 'center', fontWeight: 'bold' }}>
                    {item.rate != null ? formatCurrencyValue(item.rate) : ''}
                  </Text>
                </View>
                <View style={[styles.tdQty, { backgroundColor: rowBackgrounds.right, justifyContent: 'center', textAlign: 'center' }]}>
                  <Text style={{ fontSize: 10, color: COLORS.black, textAlign: 'center', fontWeight: 'bold' }}>
                    {formatQuantityValue(item.quantity)}
                  </Text>
                </View>
                <View style={[styles.tdTotal, { backgroundColor: rowBackgrounds.right }]}>
                  <Text>{formatCurrencyValue(item.total)}</Text>
                </View>
                <View style={[styles.tdExcl, { backgroundColor: rowBackgrounds.right }]}>
                  <Text>{item.exclGst === false ? '' : 'Excl. GST'}</Text>
                </View>
              </View>
            );
          })}

          {/* Filler rows (keep 7 cells) */}
          {Array.from({ length: fillerRowCount }).map((_, index) => {
            const rowBackgrounds = getInvoiceRowBackgrounds(invoiceItems.length + index, theme);

            return (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tdProject, { backgroundColor: rowBackgrounds.left }]} />
                <View style={[styles.tdLocation, { backgroundColor: rowBackgrounds.left }]} />
                <View style={[styles.tdItem, { backgroundColor: rowBackgrounds.left }]} />
                <View style={[styles.tdRate, { backgroundColor: rowBackgrounds.right }]} />
                <View style={[styles.tdQty, { backgroundColor: rowBackgrounds.right }]} />
                <View style={[styles.tdTotal, { backgroundColor: rowBackgrounds.right }]} />
                <View style={[styles.tdExcl, { backgroundColor: rowBackgrounds.right }]} />
              </View>
            );
          })}
        </View>

        {/* Payment + totals */}
        <View style={styles.paymentSection}>
          <View style={styles.paymentMethods}>
            <Text style={styles.paymentTitle}>Payment Methods</Text>
            {senderProfile.payId ? (
              <Text style={styles.paymentText}>PayID: {senderProfile.payId}</Text>
            ) : null}
            <Text style={styles.paymentText}>
              <Text style={{ fontWeight: 'bold' }}>Bank Transfer:</Text> BSB: {senderProfile.bsb}    Account No: {senderProfile.accountNumber}
            </Text>
            <Text style={styles.paymentText}>
              <Text style={{ fontWeight: 'bold' }}>Account Name:</Text> {senderProfile.accountName}
            </Text>
            <Text style={[styles.paymentText, styles.referenceText, { color: theme.reference }]}>
              Please use reference: {paymentReference}
            </Text>
            {data.rfiTicketNumber ? (
              <Text style={styles.paymentText}>RFI Ticket: {data.rfiTicketNumber}</Text>
            ) : null}
            {visibleStripePaymentUrl ? (
              <Link
                src={visibleStripePaymentUrl}
                style={[styles.paymentLinkText, { color: theme.reference }]}
              >
                {formatStripePaymentLabelForPdf(visibleStripePaymentUrl, data.stripeSurchargeEnabled)}
              </Link>
            ) : null}
          </View>

          <View style={styles.totalsSection}>
            <View style={styles.totalRow}>
              <Text>Sub Total:</Text>
              <Text>{formatCurrencyValue(data.subtotal)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text>10% GST:</Text>
              <Text>{formatCurrencyValue(data.gstAmount)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text>Paid:</Text>
              <Text>-{formatCurrencyValue(data.paidAmount)}</Text>
            </View>
            <View style={[styles.grandTotalRow, { backgroundColor: theme.accentMid }]}>
              <Text>Grand Total:</Text>
              <Text>{formatCurrencyValue(data.totalDue)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.footerSpacer} />

        {/* Legal line */}
        {showPaymentClaim && (
          <Text style={[styles.paymentClaim, { color: theme.claim }]}>
            *This is a payment claim under the Building and Construction Industry Security of Payment Act 2002.
          </Text>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerLine}>{senderProfile.displayName}</Text>
          <Text style={styles.footerLine}>{senderProfile.address}</Text>
          <Text style={styles.footerLine}>{`M: ${senderProfile.mobile}    E: ${senderProfile.email}`}</Text>
          <Text style={styles.footerLine}>{formatInvoiceFooterReference(data.invoiceNumber)}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoiceTemplate;
